<template>
  <div class="HomeHotelAndResortsItem">
    <div class="HomeHotelAndResorts-head">
      <nuxt-link-locale :to tabindex="-2">
        <div class="HomeHotelAndResortsItem-image">
          <ai-image
            v-if="picture?.srcSet"
            :alt="picture.alt ?? ''"
            :lg="25"
            :loading="lazyLoadImage ? 'lazy' : 'eager'"
            :md="70"
            :sm="80"
            :src="picture.src"
            :src-set="picture.srcSet"
            :xl="20"
            :xs="75" />
        </div>
      </nuxt-link-locale>

      <ai-logo
        :brand="brand"
        :size="{
          height: 32,
          width: 116,
        }"
        class="HomeHotelAndResortsItem-logo" />
    </div>

    <ai-link
      :text="label"
      :to
      class="HomeHotelAndResortsItem-label"
      tabindex="-2"
      typo-html-tag-element="h3"
      typo-variant="heading-03"
      undecorated />

    <ai-location
      :city="city"
      :country="country"
      class="HomeHotelAndResortsItem-location" />

    <div
      v-if="pictograms"
      class="HomeHotelAndResortsItem-pictograms"
      role="list">
      <ai-pictograms
        v-for="(pictogram, index) in pictograms"
        :key="`HomeHotelAndResortsItem-pictograms-${pictogram.label}-${index}`"
        :label="pictogram.label"
        :show-tool-tip="true"
        :size="24"
        :url="pictogram.url"
        class="HomeHotelAndResortsItem-pictogramsItem"
        icon-position="center"
        inline
        role="listitem"
        variant="positive" />
    </div>

    <ai-link
      :aria-label="
        $t('homepage.hotelAndResorts.ctaLabel', { hotelName: label })
      "
      :text="ctaLabel"
      :to
      class="HomeHotelAndResortsItem-cta"
      color="gold-900"
      icon-right="button-arrow-right" />
  </div>
</template>

<script lang="ts" setup>
import { RouteLocationRaw } from 'vue-router';

import type { AiPicture } from '~/domains/graphql';
import type { Pictogram } from '~/domains/ux/molecules/AiCard/AiHotelCard.vue';

type Props = {
  brand?: string;
  city: string;
  country: string;
  ctaLabel: string;
  label: string;
  lazyLoadImage: boolean;
  pictograms?: Pictogram[];
  picture?: AiPicture | null;
  to: RouteLocationRaw;
};

defineProps<Props>();
</script>

<style lang="scss" scoped>
@use '@/assets/styles/utilities/constants';
@use '@/assets/styles/utilities/mixins';
@use '@/assets/styles/utilities/colors';

.HomeHotelAndResortsItem {
  display: flex;
  height: 100%;
  width: 100%;
  flex-direction: column;
  justify-content: space-between;
}

.HomeHotelAndResorts-head {
  margin-bottom: constants.$margin-01;
}

.HomeHotelAndResortsItem-label {
  margin-bottom: constants.$inner-03;
}

.HomeHotelAndResortsItem-location {
  margin-bottom: constants.$margin-02;
}

.HomeHotelAndResortsItem-pictograms {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: flex-start;
  row-gap: constants.$inner-02;
  margin-bottom: constants.$margin-03;
}

.HomeHotelAndResortsItem-cta {
  text-decoration: none;
  text-transform: uppercase;
  color: colors.$gold-900;
}

.HomeHotelAndResortsItem-image {
  margin-bottom: constants.$margin-02;
  @include mixins.rem-fallback(height, 212);
}

.HomeHotelAndResortsItem-pictogramsItem {
  margin-right: constants.$margin-01;

  &:last-child {
    margin-right: 0;
  }
}
</style>
